import { useContext, useState } from 'react';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { MessageSquare, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Shown ONCE after a legacy customer logs in, asking whether they want to
 * receive promotional SMS. Transactional SMS (OTP, order updates) is always on.
 */
export default function PromoOptInModal() {
  const { user, setUser } = useContext(AuthContext);
  const [submitting, setSubmitting] = useState(false);

  if (!user || !user.needsPromoOptIn) return null;

  const submit = async (optIn) => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('authToken');
      await axios.post(
        `${API}/customer/set-promo-opt-in`,
        { optIn },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(optIn ? "You're in — we'll text you the good stuff." : 'Got it — no promo texts.');
      // Clear the flag locally so the modal disappears
      setUser({ ...user, needsPromoOptIn: false });
    } catch (e) {
      toast.error('Could not save preference. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="promo-optin-modal"
    >
      <div className="relative w-full max-w-md rounded-2xl bg-ha-bg border border-ha-ink/10 shadow-2xl p-6 md:p-8">
        <button
          onClick={() => submit(false)}
          disabled={submitting}
          className="absolute top-3 right-3 text-ha-muted hover:text-ha-ink"
          aria-label="Close"
          data-testid="promo-optin-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-ha-green/10 flex items-center justify-center">
            <MessageSquare className="w-6 h-6 text-ha-green" />
          </div>
          <div>
            <h3 className="font-anton text-2xl uppercase text-ha-ink leading-tight">Want the good stuff?</h3>
            <p className="text-xs text-ha-muted">One-time question</p>
          </div>
        </div>

        <p className="text-sm text-ha-ink/80 mb-4">
          Get first dibs on drops, member-only promos, and live-stream alerts — straight to your phone.
        </p>

        <div className="rounded-lg bg-ha-ink/5 border border-ha-ink/10 p-3 mb-5 text-xs text-ha-muted leading-relaxed">
          By tapping <span className="font-semibold text-ha-ink">Yes</span>, you consent to receive promotional SMS from High Achievers. Msg & data rates may apply. Reply STOP any time to unsubscribe. Order updates & receipts are separate and always on.
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={() => submit(true)}
            disabled={submitting}
            data-testid="promo-optin-yes"
            className="flex-1 bg-ha-ink text-ha-bg font-anton uppercase py-5 hover:bg-ha-green hover:text-ha-ink"
          >
            Yes, text me deals
          </Button>
          <Button
            onClick={() => submit(false)}
            disabled={submitting}
            variant="outline"
            data-testid="promo-optin-no"
            className="flex-1 border-ha-ink/20 text-ha-ink hover:bg-ha-ink/5"
          >
            No thanks
          </Button>
        </div>
      </div>
    </div>
  );
}
